import { defineMessages, useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import { createGlobalState } from 'react-use';
import { useNotify } from '../../../../hooks';

const messages = defineMessages({
  awaitingProcessing: {
    id: 'UseAwaitingImageCreation.awaitingProcessing',
    defaultMessage: 'Awaiting image processing...',
  },
});

/**
 * time to wait between image creation and viewing.  15 seconds
 * should be more than plenty.  The only downside of an extra wait
 * is that the configurator waits to see the uploaded image.  Throughout
 * the entire lifespan of a survey image, this only happens once
 */
const CREATION_WAIT_TIME = 15000;

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

type ImageIDCreationHash = {
  [imageId: string]: Moment;
};

const useGlobalImageCreationHash = createGlobalState<ImageIDCreationHash>({});

/**
 * File service can take some time to register a new file and the client is responsible
 * for awaiting this.  This hook synchronizes that wait.  The only time the user will be
 * awaiting an image load is immediately after the image is configured in the builder.
 */
function useAwaitImageCreation() {
  const [globalImageIdCreationHash, setGlobalImageIdCreationHash] =
    useGlobalImageCreationHash();
  const [notify] = useNotify();
  const { formatMessage } = useIntl();

  const awaitCreation = async (imageId: string) => {
    /*
      it is highly likely this image was not even created during this session.
      If not, don't await anything
    */
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!globalImageIdCreationHash[imageId]) return;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const creationTime = globalImageIdCreationHash[imageId];
    const waitTime =
      CREATION_WAIT_TIME - moment().diff(creationTime, 'milliseconds');

    // wait time may have already passed
    if (waitTime < 0) return;

    notify(formatMessage(messages.awaitingProcessing), 'warning');
    await sleep(waitTime);
  };

  const registerCreation = (imageId: string) => {
    setGlobalImageIdCreationHash({
      ...globalImageIdCreationHash,
      [imageId]: moment(),
    });
  };

  return {
    awaitCreation,
    registerCreation,
  };
}

export default useAwaitImageCreation;
